import React, { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2, Image as ImageIcon, X, Plus, Store, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import api from "@/services/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const buildImageUrl = (rawUrl) => {
  const url = String(rawUrl || "").trim();
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  const apiBase = String(api?.defaults?.baseURL || "").replace(/\/?api\/?$/, "");
  const base = BACKEND_URL || apiBase;
  return base ? `${base}${url}` : url;
};

const CATEGORIES = [
  "Health & Wellness",
  "Beauty & Personal Care",
  "Home & Kitchen",
  "Nutrition",
  "Utilities",
];

const normalizeCategories = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value.includes("|") ? value.split("|").map((item) => item.trim()).filter(Boolean) : value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

export default function AddProductDialog({
  onCreated,
  open: controlledOpen,
  onOpenChange,
  showTrigger = true,
  triggerText = "Add Product",
  product = null,
}) {
  const { settings, refresh } = useSettings();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = typeof controlledOpen === "boolean" ? controlledOpen : uncontrolledOpen;
  const setOpen = (next) => {
    if (typeof controlledOpen !== "boolean") setUncontrolledOpen(next);
    onOpenChange && onOpenChange(next);
  };
  const [form, setForm] = useState({
    name: "",
    category: "Health & Wellness",
    price: "",
    mrp: "",
    discount_percent: "",
    gst_percent: "",
    stock: "",
    description: "",
    image_url: "",
    product_type: "metho",
    partner_id: "",
    pricing_tiers_input: "",
  });
  const [partners, setPartners] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkJson, setBulkJson] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const fileRef = useRef(null);
  const isEdit = Boolean(product?.id);

  const categories = useMemo(() => {
    const source = normalizeCategories(settings?.product_categories);
    return (source.length ? source : CATEGORIES)
      .filter(Boolean)
      .filter((value, index, arr) => arr.indexOf(value) === index);
  }, [settings?.product_categories]);

  useEffect(() => {
    if (open) api.get("/partners").then(r => setPartners(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!categories.includes(form.category)) {
      setForm((current) => ({ ...current, category: categories[0] || "Health & Wellness" }));
    }
  }, [categories, form.category]);

  useEffect(() => {
    if (!open) return;
    if (product?.id) {
      setForm({
        name: product.name || "",
        category: product.category || (categories[0] || "Health & Wellness"),
        price: String(product.price ?? ""),
        mrp: String(product.mrp ?? product.price ?? ""),
        discount_percent: String(product.discount_percent ?? ""),
        gst_percent: String(product.gst_percent ?? ""),
        stock: String(product.stock ?? ""),
        description: product.description || "",
        image_url: product.image_url || "",
        product_type: product.product_type || "metho",
        partner_id: product.partner_id || "",
        pricing_tiers_input: Array.isArray(product.pricing_tiers)
          ? product.pricing_tiers.map((t) => `${t.qty}:${t.price}`).join(", ")
          : "",
      });
      return;
    }
    resetForm();
  }, [open, product?.id, categories]);

  useEffect(() => {
    const mrp = Number(form.mrp || 0);
    const discount = Number(form.discount_percent || 0);
    if (mrp > 0) {
      const nextPrice = Math.max(0, mrp * (1 - (discount / 100)));
      setForm((current) => ({ ...current, price: String(Number(nextPrice.toFixed(2))) }));
    }
  }, [form.mrp, form.discount_percent]);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const resetForm = () => setForm({
    name: "", category: categories[0] || "Health & Wellness", price: "", mrp: "", discount_percent: "", gst_percent: "", stock: "",
    description: "", image_url: "", product_type: "metho", pricing_tiers_input: "",
  });

  const parsePricingTiers = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return [];
    const rows = text.split(",").map((x) => x.trim()).filter(Boolean);
    const map = {};
    for (const row of rows) {
      const [qtyRaw, priceRaw] = row.split(":").map((x) => x.trim());
      const qty = Number(qtyRaw);
      const price = Number(priceRaw);
      if (!Number.isFinite(qty) || !Number.isFinite(price) || qty <= 0 || price <= 0) continue;
      map[Math.trunc(qty)] = Number(price.toFixed(2));
    }
    return Object.keys(map)
      .map((qty) => ({ qty: Number(qty), price: map[qty] }))
      .sort((a, b) => a.qty - b.qty);
  };

  const saveCategories = async (nextCategories, successMessage) => {
    setSavingCategory(true);
    try {
      await api.put("/settings", { product_categories: nextCategories });
      refresh();
      toast.success(successMessage);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Category update failed");
      throw err;
    } finally {
      setSavingCategory(false);
    }
  };

  const addCategory = async () => {
    const trimmed = newCategory.trim();
    if (!trimmed) {
      toast.error("নতুন category name দিন");
      return;
    }
    const exists = categories.some((c) => c.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setForm((current) => ({ ...current, category: categories.find((c) => c.toLowerCase() === trimmed.toLowerCase()) || trimmed }));
      setNewCategory("");
      toast.error("এই category আগেই আছে");
      return;
    }
    try {
      const nextCategories = [...categories, trimmed].sort((a, b) => a.localeCompare(b));
      await saveCategories(nextCategories, "New category added");
      setForm((current) => ({ ...current, category: trimmed }));
      setNewCategory("");
    } catch (err) {
      return;
    }
  };

  const startRenameCategory = (category) => {
    setEditingCategory(category);
    setRenameValue(category);
  };

  const renameCategory = async (category) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      toast.error("নতুন category name দিন");
      return;
    }
    const duplicate = categories.some((item) => item !== category && item.toLowerCase() === trimmed.toLowerCase());
    if (duplicate) {
      toast.error("এই category name আগেই আছে");
      return;
    }
    try {
      const nextCategories = categories.map((item) => (item === category ? trimmed : item));
      await saveCategories(nextCategories, "Category renamed");
      if (form.category === category) {
        setForm((current) => ({ ...current, category: trimmed }));
      }
      setEditingCategory("");
      setRenameValue("");
    } catch (err) {
      return;
    }
  };

  const deleteCategory = async (category) => {
    if (categories.length <= 1) {
      toast.error("শেষ category delete করা যাবে না");
      return;
    }
    if (!window.confirm(`"${category}" category delete করতে চান?`)) return;
    const nextCategories = categories.filter((item) => item !== category);
    try {
      await saveCategories(nextCategories, "Category deleted");
      if (form.category === category) {
        setForm((current) => ({ ...current, category: nextCategories[0] || "Health & Wellness" }));
      }
      if (editingCategory === category) {
        setEditingCategory("");
        setRenameValue("");
      }
    } catch (err) {
      return;
    }
  };

  const moveCategory = async (category, direction) => {
    const currentIndex = categories.indexOf(category);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= categories.length) return;
    const nextCategories = [...categories];
    [nextCategories[currentIndex], nextCategories[nextIndex]] = [nextCategories[nextIndex], nextCategories[currentIndex]];
    try {
      await saveCategories(nextCategories, "Category order updated");
    } catch (err) {
      return;
    }
  };

  const generateDescription = async () => {
    if (!form.name.trim()) {
      toast.error("প্রথমে product name লিখুন");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/products/generate-description", {
        name: form.name,
        category: form.category,
        product_type: form.product_type,
        partner_id: form.product_type === "associate_partner" ? (form.partner_id || null) : null,
      });
      setForm(f => ({ ...f, description: data.description }));
      toast.success("AI description generated ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File 5MB এর কম হতে হবে");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = buildImageUrl(data?.url);
      setForm(x => ({ ...x, image_url: fullUrl }));
      toast.success("Image uploaded ✓");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.price) {
      toast.error("Name, category, price দরকার");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        pricing_tiers: parsePricingTiers(form.pricing_tiers_input),
        name: form.name,
        category: form.category,
        price: Number(form.price || form.mrp || 0),
        mrp: Number(form.mrp || form.price || 0),
        discount_percent: Number(form.discount_percent || 0),
        gst_percent: form.product_type === "metho" ? Number(form.gst_percent || 0) : 0,
        bv: 0,
        stock: Number(form.stock || 0),
        description: form.description || "",
        image_url: form.image_url || "",
        product_type: form.product_type,
        partner_id: form.product_type === "associate_partner" ? (form.partner_id || null) : null,
      };
      const { data } = isEdit
        ? await api.put(`/products/${product.id}`, payload)
        : await api.post("/products", payload);
      if (isEdit) {
        toast.success("Product updated!");
      } else if (data?.product_code) {
        toast.success(`Product added! Code: ${data.product_code}`);
      } else {
        toast.success("Product added!");
      }
      resetForm();
      setOpen(false);
      onCreated && onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const importBulkProducts = async () => {
    const raw = String(bulkJson || "").trim();
    if (!raw) {
      toast.error("Bulk JSON দিন");
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      toast.error("Invalid JSON");
      return;
    }

    const rows = Array.isArray(parsed) ? parsed : parsed?.rows;
    if (!Array.isArray(rows) || rows.length === 0) {
      toast.error("rows array required");
      return;
    }

    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk-create", { rows });
      const created = Number(data?.created_count || 0);
      const failed = Number(data?.failed_count || 0);
      if (failed > 0) {
        const firstErr = (Array.isArray(data?.errors) && data.errors[0]) ? data.errors[0] : null;
        toast.error(`Bulk: ${created} created, ${failed} failed${firstErr ? ` (row ${Number(firstErr.index) + 1}: ${firstErr.error})` : ""}`);
      } else {
        toast.success(`Bulk import success: ${created} products created`);
      }
      setBulkJson("");
      onCreated && onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {showTrigger ? (
        <DialogTrigger asChild>
          <Button className="bg-emerald-900 hover:bg-emerald-950 rounded-full" data-testid="add-product-button">
            <Plus className="w-4 h-4 mr-2" /> {triggerText}
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Product Edit করুন" : "নতুন Product যোগ করুন"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Edit product তথ্য আপডেট করুন" : "নতুন product details দিয়ে save করুন"}
          </DialogDescription>
        </DialogHeader>
        {isEdit ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="edit-flow-note">
            আগে Product-এর তথ্য edit করে <b>Update Product</b> দিন। পরে আবার Edit খুলে একইভাবে একটার পর একটা image upload/change করতে পারবেন।
          </div>
        ) : null}
        <form onSubmit={save} className="space-y-4" data-testid="add-product-form">
          {!isEdit ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="bulk-product-import-panel">
              <p className="text-xs uppercase tracking-wider text-amber-900 font-semibold">METHO Bulk Product Import</p>
              <p className="text-[11px] text-amber-900/80 mt-1">JSON array paste করুন। Required fields: name, category, mrp অথবা price। Optional: stock, description, image_url, discount_percent, gst_percent, product_type, partner_id, pricing_tiers.</p>
              <Textarea
                value={bulkJson}
                onChange={(e) => setBulkJson(e.target.value)}
                placeholder='[{"name":"METHO Honey","category":"Nutrition","mrp":650,"stock":50}]'
                rows={5}
                className="mt-2 bg-white"
                data-testid="bulk-product-json-input"
              />
              <div className="mt-2 flex justify-end">
                <Button type="button" variant="outline" className="rounded-full" disabled={bulkBusy} onClick={importBulkProducts} data-testid="bulk-product-import-button">
                  {bulkBusy ? "Importing..." : "Import Bulk Products"}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Product Name *</Label>
              <Input
                required
                value={form.name}
                onChange={setF("name")}
                placeholder="e.g., METHO Organic Turmeric"
                data-testid="new-product-name-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="new-product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="mt-2 flex gap-2">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="New category name"
                  data-testid="new-category-input"
                />
                <Button
                  type="button"
                  onClick={addCategory}
                  disabled={savingCategory}
                  variant="outline"
                  className="rounded-full"
                  data-testid="add-category-button"
                >
                  {savingCategory ? "Adding..." : "Add Category"}
                </Button>
              </div>
              <div className="mt-3 space-y-2 rounded-xl border border-border bg-slate-50 p-3" data-testid="category-manager">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Manage Categories</p>
                {categories.map((category, index) => (
                  <div key={category} className="flex items-center gap-2">
                    {editingCategory === category ? (
                      <Input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        data-testid={`rename-category-input-${category.replace(/\s+/g, "-").toLowerCase()}`}
                      />
                    ) : (
                      <div className="flex-1 rounded-lg border border-border bg-white px-3 py-2 text-sm text-slate-700">{category}</div>
                    )}
                    {editingCategory === category ? (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full"
                          disabled={savingCategory}
                          onClick={() => renameCategory(category)}
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="rounded-full"
                          onClick={() => { setEditingCategory(""); setRenameValue(""); }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full"
                          disabled={savingCategory || index === 0}
                          onClick={() => moveCategory(category, -1)}
                          data-testid={`move-up-category-button-${category.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full"
                          disabled={savingCategory || index === categories.length - 1}
                          onClick={() => moveCategory(category, 1)}
                          data-testid={`move-down-category-button-${category.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full"
                          disabled={savingCategory}
                          onClick={() => startRenameCategory(category)}
                          data-testid={`rename-category-button-${category.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Rename
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full border-red-200 text-red-700 hover:bg-red-50"
                          disabled={savingCategory}
                          onClick={() => deleteCategory(category)}
                          data-testid={`delete-category-button-${category.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>MRP (₹) *</Label>
              <Input required type="number" value={form.mrp} onChange={setF("mrp")} data-testid="new-product-mrp-input" className="mt-1.5" />
            </div>
            <div>
              <Label>Discount %</Label>
              <Input type="number" value={form.discount_percent} onChange={setF("discount_percent")} data-testid="new-product-discount-input" className="mt-1.5" placeholder="0" />
            </div>
            <div>
              <Label>Final Price (₹) *</Label>
              <Input required type="number" value={form.price} onChange={setF("price")} data-testid="new-product-price-input" className="mt-1.5" />
            </div>
            <div>
              <Label>Stock</Label>
              <Input type="number" value={form.stock} onChange={setF("stock")} data-testid="new-product-stock-input" className="mt-1.5" placeholder="0" />
            </div>
          </div>

          {form.product_type === "metho" && (
            <div>
              <Label>GST % (only METHO product)</Label>
              <Input type="number" value={form.gst_percent} onChange={setF("gst_percent")} data-testid="new-product-gst-input" className="mt-1.5 max-w-xs" placeholder="e.g. 18" />
            </div>
          )}

          {(form.product_type === "metho" || form.product_type === "associate_partner") && (
            <div>
              <Label>Tier Pricing (pcs:price)</Label>
              <Input
                value={form.pricing_tiers_input}
                onChange={setF("pricing_tiers_input")}
                data-testid="new-product-tier-pricing-input"
                className="mt-1.5"
                placeholder="1:20, 3:50, 6:90, 12:170"
              />
              <p className="text-[11px] text-muted-foreground mt-1.5 font-body">
                উদাহরণ: 1:20, 3:50, 6:90, 12:170, 24:320
              </p>
            </div>
          )}

          <div>
            <Label>Product Type</Label>
            <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v, partner_id: v === "metho" ? "" : form.partner_id })}>
              <SelectTrigger className="mt-1.5" data-testid="new-product-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="metho">METHOO STORE Product (Smart Cycle qualified)</SelectItem>
                <SelectItem value="associate_partner">Associate Partner Product</SelectItem>
              </SelectContent>
            </Select>
            {form.product_type === "associate_partner" && (
              <div className="mt-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
                <Label className="flex items-center gap-1.5 text-amber-900"><Store className="w-3.5 h-3.5" /> Link to Partner <span className="text-red-500">*</span></Label>
                <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="product-partner-select">
                    <SelectValue placeholder="Select an Associate Partner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-slate-500">
                        No partners yet — <a href="/app/partners" className="text-emerald-800 underline">register one first</a>
                      </div>
                    ) : (
                      partners.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.business_name} · {p.commission_percent}% commission
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {form.partner_id && (() => {
                  const p = partners.find(x => x.id === form.partner_id);
                  return p ? <p className="text-[11px] text-amber-900 mt-2">
                    Commission on every sale: <b>{p.commission_percent}%</b> → contributes to Company + Pools
                  </p> : null;
                })()}
              </div>
            )}
          </div>

          {/* Description with AI generator */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label>Description</Label>
              <Button
                type="button"
                onClick={generateDescription}
                disabled={generating || !form.name.trim()}
                variant="outline"
                size="sm"
                className="h-8 text-xs rounded-full border-amber-400 text-amber-800 hover:bg-amber-50"
                data-testid="ai-generate-description-button"
              >
                {generating ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-3 h-3 mr-1" /> AI দিয়ে লিখুন</>
                )}
              </Button>
            </div>
            <Textarea
              value={form.description}
              onChange={setF("description")}
              placeholder="Product description... (AI দিয়েও লেখানো যাবে)"
              rows={3}
              data-testid="new-product-description-input"
            />
          </div>

          {/* Image upload */}
          <div>
            <Label>Product Image (mobile/PC থেকে upload)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={uploadImage}
                className="hidden"
                id="product-image-input"
                data-testid="new-product-image-input"
              />
              <Button
                type="button"
                onClick={() => fileRef.current?.click()}
                variant="outline"
                disabled={uploading}
                className="rounded-full border-emerald-900/20 hover:bg-emerald-50"
                data-testid="upload-image-button"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Choose Image</>
                )}
              </Button>
              {form.image_url ? (
                <div className="relative">
                  <img
                    src={buildImageUrl(form.image_url)}
                    alt="Preview"
                    className="w-20 h-20 rounded-lg object-cover border border-border"
                    data-testid="uploaded-image-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: "" })}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 rounded-lg bg-secondary/50 border border-dashed border-border flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-slate-400" />
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5 font-body">JPG / PNG / WebP · max 5 MB</p>
            {isEdit ? (
              <p className="text-[11px] text-emerald-700 mt-1 font-body">Image change করতে চাইলে নতুন image upload করে Update Product দিলেই পুরোনো image replace হবে।</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="submit"
              disabled={saving}
              className="bg-emerald-900 hover:bg-emerald-950 rounded-full"
              data-testid="save-product-button"
            >
              {saving ? "Saving..." : (isEdit ? "Update Product" : "Save Product")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

